import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux/es/exports";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Select from "react-select";
import axios from "axios";
import { fetchPostalCodes } from "../../redux/Actions";
import {
  fetchAddressByPincode,
  fetchGetShopByPinCode,
  fetchSaveAddress,
} from "../../redux/Actions/checkoutPageActions";
import { setValue } from "../../utility";
import "./addressPromptModal.css";

const UKPostalCode: RegExp =
  /([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})/;

const searchPinCodeFormSchema = yup.object().shape({
  pinCode: yup
    .string()
    .required("Pin code is required")
    .matches(UKPostalCode, "Enter a valid Pin code")
    .trim()
    .max(8, "Enter a valid Pin code"),
});

const addressFormSchema = yup.object().shape({
  address: yup
    .object()
    .shape({
      label: yup.string().required("Address is required to proceed"),
      value: yup.string().required("Address is required to proceed"),
    })
    .test("address.value", "Address is required to proceed", (value: any) => {
      if (value.value == "") {
        return false;
      } else return true;
    })
    .required("Address is required to proceed"),
});

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

function AddressPromptModal({ isOpen, onClose, onSuccess }: Props) {
  const dispatch = useDispatch<any>();
  const state: any = useSelector((state) => state);
  const areaOptions: any = useSelector<any>(
    (state) => state?.addressByPincode?.data?.address_list
  );

  const [postalCodeList, setPostalCodeList] = useState<any[]>([]);
  const [postalCodeValue, setPostalCodeValue] = useState("");
  const [checkWithNewPin, setCheckWithNewPin] = useState(true);
  const [selectedAddress, setSelectedAddress] = useState<any>(null);
  const [coveredArea, setCoveredArea] = useState(false);
  const [areaData, setAreaData] = useState<any[]>([]);

  const {
    handleSubmit: handlePinCodeSubmit,
    control: pinCodeFormControl,
    setError: setPinCodeFormError,
    setValue: setPinCodeFormValue,
    formState: { errors: pinCodeFormError },
  } = useForm({
    resolver: yupResolver(searchPinCodeFormSchema),
    mode: "onChange",
    defaultValues: { pinCode: "" },
  });

  const {
    control: addressFormControl,
    setError: addressFormSetError,
    setValue: setAddressFormValue,
    getValues: getValuesAddressForm,
    reset: resetAddressForm,
    clearErrors,
    formState: { errors: addressFormError },
  } = useForm({
    resolver: yupResolver(addressFormSchema),
    mode: "onChange",
    defaultValues: {
      address: { value: "", label: "Select your address..." },
    },
  });

  useEffect(() => {
    if (state) setPostalCodeList(state.postalCodeList || []);
  }, [state]);

  useEffect(() => {
    if (!isOpen) {
      setCheckWithNewPin(true);
      setPostalCodeValue("");
      setSelectedAddress(null);
      setCoveredArea(false);
      setPinCodeFormValue("pinCode", "");
      resetAddressForm();
    }
  }, [isOpen]);

  useEffect(() => {
    if (checkWithNewPin == false) {
      setPinCodeFormValue("pinCode", "");
      setSelectedAddress(null);
      resetAddressForm();
    }
  }, [checkWithNewPin]);

  const fetchCoveredAreaCodes = async () => {
    const base_url = process.env.REACT_APP_BACKEND_URL;
    try {
      const response = await axios({
        method: "get",
        url: `${base_url}/api/areas_covered_postcodes`,
        headers: {
          Accept: "application/json",
          "Access-Control-Allow-Methods": "GET, POST",
          "Access-Control-Allow-Origin": "*",
        },
      });
      if (response.data.Response.response_code == "1") {
        setAreaData(response.data.data);
      }
    } catch (error) {}
  };

  const handleChange = (e: any) => {
    setCoveredArea(false);
    const { value } = e.target;
    const updateValue = value.replace(/\s/g, "");
    setPinCodeFormValue("pinCode", updateValue);
    if (value.length > 0) dispatch(fetchPostalCodes(updateValue));
    setPostalCodeValue(updateValue);
  };

  const onSearch = (searchTerm: any) => {
    setPinCodeFormValue("pinCode", searchTerm);
    setPostalCodeValue(searchTerm);
    setValue("postalCode", searchTerm.toUpperCase());
  };

  const handleAddressChange = async (selected: any) => {
    setSelectedAddress(selected);
    setAddressFormValue("address", selected);
    if (getValuesAddressForm("address")?.value != "") clearErrors();
  };

  const submitPostalForm = async (data: any) => {
    localStorage.setItem("postalCode", JSON.stringify(data.pinCode));
    const shop = await dispatch(
      fetchGetShopByPinCode(JSON.stringify(data.pinCode))
    );
    if (shop) {
      setCoveredArea(false);
      const result = await dispatch(fetchAddressByPincode());
      if (result.status == 200 && result.data.data?.address_list.length > 0) {
        setCheckWithNewPin(false);
      } else {
        setPinCodeFormError("pinCode", {
          type: "server",
          message: "Area not covered",
        });
        await fetchCoveredAreaCodes();
        setCoveredArea(true);
      }
    } else {
      setPinCodeFormError("pinCode", {
        type: "server",
        message: "Area not covered",
      });
      await fetchCoveredAreaCodes();
      setCoveredArea(true);
    }
  };

  const persistSuggestion = (postalCode: string) => {
    let addressSuggestions: any = [{ ...selectedAddress }];
    addressSuggestions[0].postalCode = postalCode;
    if (localStorage.getItem("addressSuggestions")) {
      const existing = JSON.parse(
        localStorage.getItem("addressSuggestions") || "[]"
      );
      addressSuggestions = addressSuggestions.concat(existing);
    }
    localStorage.setItem(
      "addressSuggestions",
      JSON.stringify(addressSuggestions)
    );
  };

  const submitAddressChange = async () => {
    const postalCode: any = localStorage.getItem("postalCode");
    if (getValuesAddressForm("address")?.value == "") {
      addressFormSetError("address", {
        type: "server",
        message: "Address is required to proceed",
      });
      return;
    }
    clearErrors();
    localStorage.setItem(
      "selectedAddressBeforeLogin",
      JSON.stringify(selectedAddress)
    );
    persistSuggestion(postalCode);

    if (localStorage.getItem("token")) {
      try {
        const userDetails = JSON.parse(localStorage.getItem("userDetails")!);
        const contact_name = userDetails?.data?.user_info?.name;
        const mobile_number = userDetails?.data?.user_info?.mobile;
        const address = selectedAddress?.value?.split(",");
        const payloadData = {
          address_type: "pickup",
          address_details: `{"contact_name":"${contact_name}","mobile_number":"${mobile_number}","line_1":"${address[0].trim()}","line_2":"${address[1].trim()}","line_3":"${address[2].trim()}","line_4":"${address[3].trim()}","locality":"${address[4].trim()}","city":"${address[5].trim()}","county":"${address[6].trim()}","label":"Home","pincode":"${JSON.parse(
            postalCode
          )}"}`,
        };
        const uniqueAddress = await dispatch(fetchSaveAddress(payloadData));
        if (
          uniqueAddress.status ||
          uniqueAddress.message == "Address already exists"
        ) {
          onSuccess();
        } else {
          addressFormSetError("address", {
            type: "server",
            message: "Unable to set address try again later",
          });
        }
      } catch (e) {
        onSuccess();
      }
    } else {
      onSuccess();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="address-prompt-modal-overlay" onClick={onClose}>
      <div
        className="address-prompt-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="address-prompt-modal-header">
          <h4>Enter your address to proceed</h4>
          <button
            type="button"
            className="address-prompt-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>
        <div className="address-prompt-modal-body">
          <p className="address-prompt-modal-sub">
            We need your postcode &amp; address before placing an order.
          </p>

          <form onSubmit={handlePinCodeSubmit(submitPostalForm)}>
            <div className={`${checkWithNewPin == false ? "d-none" : ""}`}>
              <small className="text-danger postvalidation">
                {pinCodeFormError.pinCode?.message}
              </small>
              <div className="postcodeform">
                <Controller
                  name="pinCode"
                  control={pinCodeFormControl}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      id="postcode-input-modal"
                      value={postalCodeValue}
                      placeholder="Enter the postal code"
                      className={`ui-autocomplete-input ${
                        pinCodeFormError.pinCode ? "invalid" : ""
                      }`}
                      onChange={handleChange}
                      autoComplete="off"
                    />
                  )}
                />
                <button
                  className="btn hover-btn"
                  id="book_now_btn_modal"
                  type="submit"
                >
                  Search
                </button>
                <div className="dropdown">
                  {postalCodeList
                    .filter((item: any) => {
                      const searchTerm = postalCodeValue.toLowerCase();
                      const mainValue = item.postcode.toLowerCase();
                      return (
                        searchTerm &&
                        mainValue.startsWith(searchTerm) &&
                        mainValue !== searchTerm
                      );
                    })
                    .slice(0, 10)
                    .map((item: any) => (
                      <div
                        onClick={() => onSearch(item.postcode)}
                        className="dropdown-row"
                        key={item.postcode}
                      >
                        <i className="fas fa-map-marker-alt"></i>
                        {item.postcode}
                      </div>
                    ))}
                </div>
                {coveredArea && (
                  <div className="dropdown">
                    <div className="dropdown-heading">
                      Postcode areas we currently cover
                    </div>
                    {areaData.map((item: any) => (
                      <div
                        onClick={() => {
                          onSearch(item);
                          setCoveredArea(false);
                        }}
                        className="dropdown-row"
                        key={item}
                      >
                        <i className="fas fa-map-marker-alt"></i>
                        {item}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </form>

          <div
            className={`adrsselectform ${
              checkWithNewPin == true ? "d-none" : ""
            }`}
          >
            <small className="text-danger postvalidation">
              {addressFormError.address?.message}
            </small>
            <div className="adrsselectdd">
              <Controller
                name="address"
                control={addressFormControl}
                render={({ field }) => (
                  <Select
                    placeholder="Select your address"
                    {...field}
                    value={selectedAddress}
                    className={`${
                      addressFormError.address ? "invalid" : ""
                    }`}
                    onChange={handleAddressChange}
                    onFocus={() => {
                      if (getValuesAddressForm("address").value == "") {
                        addressFormSetError("address", {
                          type: "server",
                          message: "Address is required to proceed",
                        });
                      } else {
                        clearErrors();
                      }
                    }}
                    options={areaOptions}
                  />
                )}
              />
              <input
                type="button"
                value="Continue"
                className="btn hover-btn"
                onClick={submitAddressChange}
              />
            </div>
            <div className="adrsselect-or">OR</div>
            <div className="adrsselect-link">
              <a
                className="cursor-pointer"
                onClick={() => {
                  setCheckWithNewPin(true);
                  setPostalCodeValue("");
                }}
              >
                Check with another Postcode
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddressPromptModal;
